import { cva, type VariantProps } from 'class-variance-authority';
import type { ReactNode } from 'react';
import { cn } from '../../utils';
import { Icon } from '../Icon';

const alertBarVariants = cva('w-full flex items-center justify-center font-noto font-medium', {
  variants: {
    variant: {
      warning: 'border-yellow-50 bg-yellow-10',
      danger: 'border-red-50 bg-red-10',
      info: 'border-blue-40 bg-blue-10',
      success: 'border-green-50 bg-green-10',
    },
    layout: {
      inline: 'flex-col rounded border p-2 text-xs',
      // Full-width strip under page chrome: one line at a fixed height, overflowing text truncated.
      banner: 'h-8 min-w-0 flex-row gap-2 border-b px-4 text-sm',
    },
  },
  defaultVariants: {
    variant: 'warning',
    layout: 'inline',
  },
});

export type AlertBarVariant = NonNullable<VariantProps<typeof alertBarVariants>['variant']>;

type Props = {
  title?: ReactNode;
  children?: ReactNode;
  className?: string;
  icon?: 'info' | 'warning' | 'danger' | 'success' | null;
  iconSize?: number;
} & VariantProps<typeof alertBarVariants>;

const severityLabel: Record<NonNullable<Props['icon']>, string> = {
  danger: 'Error',
  success: 'Success',
  warning: 'Warning',
  info: 'Info',
};

/**
 * AlertBar
 *
 * Lightweight alert for contextual notices (e.g., review flags). `variant` sets the color; `layout="banner"`
 * renders a full-width single-line strip for page-level notices.
 */
export function AlertBar({ title, children, className, variant, layout, icon, iconSize = 14 }: Props) {
  const isBanner = layout === 'banner';
  const iconName =
    icon === 'danger'
      ? 'crossCircled'
      : icon === 'success'
        ? 'checkCircled'
        : icon === 'warning'
          ? 'questionMarkCircled'
          : 'infoCircled';

  return (
    <div className={cn(alertBarVariants({ variant, layout }), className)}>
      <div className={cn('flex gap-2', isBanner ? 'shrink-0 items-center' : 'items-start')}>
        {icon && (
          <Icon
            icon={iconName}
            size={iconSize}
            className={cn('text-primary-dark', !isBanner && 'mt-[1px]')}
            ariaLabel={severityLabel[icon]}
          />
        )}
        {title ? <div className="text-primary-dark text-sm font-semibold">{title}</div> : null}
      </div>
      {children ? <div className={cn('text-grey-80', isBanner && 'min-w-0 truncate')}>{children}</div> : null}
    </div>
  );
}
